import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ListCard } from 'melp-design/components';
import getPriceLabel from 'components/marketplace/ItemCard/common/getPriceLabel';
import { ShopItemDetailsModal } from 'components/marketplace/shop-item-details-modal';
import { useModalContext } from 'store/modal';
import { useCompanyLanguages, useMe } from 'state/Administrators';
import { resolveTranslation, stripHtml } from 'utils/general';
import { ShopItemByBenefit } from 'store/shop';
import { useAssignShopItem, useUnassignShopItem } from 'store/benefits';
import { FC } from 'react';

interface Props {
  shopItem: ShopItemByBenefit;
  benefitId: string;
  disabled: boolean;
}

const ItemCard: FC<Props> = ({ shopItem, benefitId, disabled }) => {
  const { t, i18n } = useTranslation();
  const { defaultLanguage } = useCompanyLanguages();
  const { openModal } = useModalContext();
  const { canEditBenefits } = useMe();

  const translation = resolveTranslation(
    shopItem.translations,
    i18n.language,
    defaultLanguage,
  );

  const { mutate: assignShopItem, isLoading: isAssigning } =
    useAssignShopItem();
  const { mutate: unassignShopItem, isLoading: isUnassigning } =
    useUnassignShopItem();

  const handleItemSelection = () => {
    assignShopItem(
      { benefitId, shopItemId: shopItem.id },
      {
        onSuccess: () => {
          toast.success(t('marketplace.itemsAdditionSuccess', { count: 1 }));
        },
      },
    );
  };

  const handleItemRemoval = () => {
    unassignShopItem(
      { benefitId, shopItemId: shopItem.id },
      {
        onSuccess: () => {
          toast.success(t('marketplace.itemsRemovalSuccess', { count: 1 }));
        },
      },
    );
  };

  const isAutoAssigned = !!shopItem.assignment?.autoAssigned;
  const isDisabled = !canEditBenefits || disabled;
  const isSelected = !!shopItem.assignment;

  return (
    <ListCard
      title={translation?.title}
      description={stripHtml(translation?.description ?? '')}
      label={getPriceLabel(shopItem)}
      image={shopItem.image?.url}
      onClick={() => {
        openModal({
          component: ShopItemDetailsModal,
          size: 'lg',
          props: {
            item: {
              title: translation?.title ?? '',
              description: translation?.description ?? '',
              benefitCategories: shopItem.benefitPlanTypes,
              categories: shopItem.categories,
              countries: shopItem.countries,
              cities: shopItem.cities,
              currency: shopItem.currency,
              prices: shopItem.prices,
              supplier: {
                name: shopItem.supplier.name,
                termsAndConditionsLink:
                  shopItem.supplier.termsAndConditionsLink,
              },
              image: shopItem.image?.url,
              logo: shopItem.supplier.logo?.url,
            },
            actions: [
              isSelected
                ? {
                    variant: 'danger-fill',
                    label: t('marketplace.removeItem'),
                    onClick: handleItemRemoval,
                    disabled: isAutoAssigned || isDisabled || isUnassigning,
                    tooltip: isAutoAssigned
                      ? t('marketplace.autoAssignedNotRemovable')
                      : undefined,
                  }
                : {
                    variant: 'primary',
                    onClick: handleItemSelection,
                    label: t('marketplace.selectItem'),
                    disabled: isDisabled || isAssigning,
                  },
            ],
          },
        });
      }}
      highlighted={isSelected}
      action={
        isSelected
          ? {
              label: t('marketplace.removeItem'),
              variant: 'danger-outline',
              onClick: handleItemRemoval,
              disabled: isAutoAssigned || isDisabled || isUnassigning,
              tooltip: isAutoAssigned
                ? t('marketplace.autoAssignedNotRemovable')
                : undefined,
            }
          : {
              label: t('marketplace.selectItem'),
              variant: 'secondary',
              onClick: handleItemSelection,
              disabled: isAutoAssigned || isDisabled || isAssigning,
            }
      }
    />
  );
};

export default ItemCard;
